import {
  V2ListResourceRatesForAccountApiArg,
  useV2ListResourceRatesForAccountQuery,
} from "@generated";
import type { ResourceRate } from "../types";

export interface UseResourceRatesListArgs {
  rateTableId: string;
  page?: V2ListResourceRatesForAccountApiArg["page"];
}

export interface ResourceRatesListPageMeta {
  recordCount: number;
  pageCount: number;
}

export const useResourceRatesList = ({
  rateTableId,
  page,
}: UseResourceRatesListArgs) => {
  const { data, error, isLoading, isFetching, refetch } =
    useV2ListResourceRatesForAccountQuery(
      {
        filter: { "rate-table": rateTableId },
        include: ["resource", "line-of-business"],
        page,
      },
      { refetchOnMountOrArgChange: true }
    );

  const included = (data as any)?.included ?? [];
  const findIncluded = (type: string, id?: string | number) =>
    included.find((i: any) => i.type === type && String(i.id) === String(id));
  const nameFor = (type: string, id?: string | number): string =>
    findIncluded(type, id)?.attributes?.name ?? "";
  // The LOB/Category column shows the section's nested-name (e.g. "Networking >
  // Cloud"), matching the form's service-category select; fall back to name.
  const nestedNameFor = (type: string, id?: string | number): string => {
    const attrs = findIncluded(type, id)?.attributes;
    return attrs?.["nested-name"] ?? attrs?.name ?? "";
  };

  const rows: ResourceRate[] = (data?.data ?? []).map((rr: any) => {
    const attrs = rr.attributes ?? {};
    const resourceId = rr.relationships?.resource?.data?.id;
    const lobId = rr.relationships?.["line-of-business"]?.data?.id;
    return {
      id: String(rr.id),
      resourceId: resourceId != null ? String(resourceId) : "",
      resourceName: nameFor("resources", resourceId),
      externalName: attrs["external-name"] ?? "",
      description: attrs.description ?? "",
      lobId: lobId != null ? String(lobId) : null,
      lobName: nestedNameFor("line-of-businesses", lobId),
      hourlyRate: attrs["hourly-rate"] ?? null,
      hourlyCost: attrs["hourly-cost"] ?? null,
      expenseRate: attrs["expense-rate"] ?? null,
      code: attrs.code ?? "",
    };
  });

  const pageMeta: ResourceRatesListPageMeta = {
    recordCount: (data as any)?.meta?.["record-count"] ?? 0,
    pageCount: (data as any)?.meta?.["page-count"] ?? 0,
  };

  return { rows, pageMeta, error, isLoading, isFetching, refetch };
};

export default useResourceRatesList;
